import {
  AbsenceListDto,
  AbsenceType,
  AppointmentEventDto,
  AppointmentType,
  QueryFactory,
  ResourceType,
} from '@lmt/shared-types';
import { AbsenceCalendarAppointment } from '@lmt/unicorn-shared/app/components/absences/AbsenceCalendarAppointment';
import { AbsencePopup } from '@lmt/unicorn-shared/app/components/absences/popup/AbsencePopup';
import { MonthViewDateHeaderCell } from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/MonthViewDateHeaderCell';
import { MonthViewTimeTableCell } from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/MonthViewTimeTableCell';
import { AppPopper } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppPopper';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { bindHover, bindPopper } from 'material-ui-popup-state';
import { usePopupState } from 'material-ui-popup-state/hooks';
import React, { FC, useCallback, useMemo, useState } from 'react';
import {
  AppointmentModel,
  AppointmentsBase,
  DXRS,
  MonthViewBase,
  useDxReactScheduler,
} from '../../common/devexpress/Scheduler/dx-react-scheduler';
import { useEventCallback } from '@mui/material';
import { useResourceDictionary } from '../../api/custom-hooks';
import { localFormat } from '../../helpers/date-helper';

interface Props {
  currentDate: Date;
}

export const AbsenceMonthCalendar: FC<Props> = (props) => {
  const { currentDate } = props;
  useDxReactScheduler();
  const [popupAbsence, setPopupAbsence] = useState<
    | {
        absence: AbsenceListDto[];
        currentDate: Date | null /* null in case of multi-day absence */;
      }
    | undefined
  >(undefined);

  const absenceQuery =
    QueryFactory.AppointmentEventsQuery.useSearchAppointmentsQuery({
      from: startOfMonth(currentDate),
      to: endOfMonth(currentDate),
      eventTypes: [AppointmentType.Absence],
    });

  const absences = (absenceQuery?.data as AbsenceListDto[]) ?? [];
  const resourceQuery = useResourceDictionary();
  const absencesAsData = useMemo(() => {
    const appointments = absences.filter(
      (e) => e.absenceType === AbsenceType.ResourceUnavailability,
    );
    const result = appointments.reduce(
      (accum, appointment) => {
        const startDateString = appointment.start.toISOString();
        const resources = resourceQuery.data ?? {};
        const typeToKey = (type?: ResourceType) =>
          type === null || type === undefined
            ? ''
            : type === ResourceType.Doctors
              ? 'doc'
              : 'places+special';
        appointment.resourceIds.forEach((resourceId) => {
          const key = startDateString + typeToKey(resources[resourceId]?.type);
          if (!accum[key])
            accum[key] = {
              startDate: appointment.start,
              endDate: appointment.end,
              data: [{ ...appointment, resourceIds: [] }],
            };
          accum[key].data[0].resourceIds.push(resourceId);
        });
        return accum;
      },
      {} as Record<
        string,
        {
          startDate: Date;
          endDate: Date;
          data: AbsenceListDto[];
        }
      >,
    );
    return Object.values(result);
  }, [absences, resourceQuery.data]);

  const onYearViewAbsenceMouseEnter = useEventCallback(
    (target: HTMLElement, absence: AbsenceListDto[] | undefined) => {
      setPopupAbsence(
        absence ? { absence: absence, currentDate: null } : undefined,
      );
    },
  );
  const onMonthViewAbsenceMouseEnter = useEventCallback(
    (
      target: HTMLElement,
      absence: AbsenceListDto[] | undefined,
      date: Date,
    ) => {
      setPopupAbsence(
        absence ? { absence: absence, currentDate: date } : undefined,
      );
    },
  );

  const popupState = usePopupState({
    variant: 'popper',
    popupId: 'absence-month-calendar',
  });
  const bindHoverRef = bindHover(popupState);

  const MonthViewCellComponent: FC<MonthViewBase.TimeTableCellProps> =
    useCallback(
      (innerProps) => (
        <MonthViewTimeTableCell
          isHolidayView={true}
          showAbsences={true}
          absences={absences}
          onHolidayMouseEnter={onMonthViewAbsenceMouseEnter}
          onMouseOver={(e) => {
            bindHoverRef.onMouseOver(e);
          }}
          onMouseLeave={(e) => {
            bindHoverRef.onMouseLeave(e);
          }}
          {...innerProps}
        />
      ),
      [absences],
    );

  // the pop-up offset for AbsenceType.Holiday is required for fixing
  // bug #54419: popup with holidays closed when trying to hover in Month view in Absences and holidays
  //
  // if we don't use useMemo, due to the animation of the pop-up appearance
  // when the cursor moves, the pop-up often reappears
  const modifiers = useMemo(() => {
    return popupAbsence?.absence.some(
      (absence) => absence.absenceType === AbsenceType.Holiday,
    )
      ? [
          {
            name: 'offset',
            options: {
              offset: [0, -90],
              // when 90 is set, the popup appears exactly under the name of the holiday,
              // and it works for any number of absences in the cell
              // in general it is 3/4 of the height of the cell in Month View (Calendar.module.scss - .month-time-table-cell)
            },
          },
        ]
      : [];
  }, [popupAbsence?.absence]);

  const AbsenceAppointmentComponent: FC<AppointmentsBase.AppointmentProps> =
    useCallback(
      (innerProps) => (
        <AbsenceCalendarAppointment
          onAbsenceMouseEnter={onYearViewAbsenceMouseEnter}
          onMouseOver={(e) => {
            bindHoverRef.onMouseOver(e);
          }}
          onMouseLeave={(e) => {
            bindHoverRef.onMouseLeave(e);
          }}
          {...innerProps}
        />
      ),
      [absences],
    );
  return (
    <div>
      <DXRS.Scheduler data={absencesAsData}>
        <DXRS.ViewState currentDate={currentDate} />
        <DXRS.MonthView
          timeTableCellComponent={MonthViewCellComponent}
          dayScaleCellComponent={(props) => (
            <MonthViewDateHeaderCell
              {...props}
              formatDate={(date) => localFormat(date as Date, 'EEEEEE')}
            />
          )}
        />
        <DXRS.Appointments appointmentComponent={AbsenceAppointmentComponent} />
      </DXRS.Scheduler>

      {popupAbsence && (
        <AppPopper
          {...bindPopper(popupState)}
          delay={300}
          modifiers={modifiers}
        >
          <AbsencePopup
            date={popupAbsence.currentDate}
            events={popupAbsence.absence}
            handleClose={popupState.close}
          />
        </AppPopper>
      )}
    </div>
  );
};

function createAppointmentModel(
  appointment: AppointmentEventDto,
): AppointmentModel {
  return {
    startDate: appointment.start,
    endDate: appointment.end,
    data: appointment,
  };
}
